export const LANG_COOKIE_NAME = "cookie_accesslang_quantera";
export const PREFS_COOKIE_NAME = "cookie_preferences_quantera";
const CONSENT_SYNC_KEY = "cookie_consent_sync_quantera";
const MAX_AGE = 31536000; // 1 năm

function cookieDomainAttr() {
  if (typeof window === "undefined") return "";
  try {
    const host = window.location.hostname;
    if (host === "quantera.vn" || host.endsWith(".quantera.vn")) {
      return "; domain=.quantera.vn";
    }
  } catch {
    // ignore
  }
  return "";
}

function safeSetCookie(cookieString) {
  try {
    document.cookie = cookieString;
  } catch {
    // Trình duyệt chặn cookie — không crash UI
  }
}

/** Xóa cookie theo tên: cả host-only và domain=.quantera.vn */
function expireCookieByName(name) {
  if (typeof document === "undefined") return;
  const base = `${name}=; path=/; max-age=0; SameSite=Lax`;
  safeSetCookie(base);
  safeSetCookie(`${base}; domain=.quantera.vn`);
  // Host-only (không domain) — phòng trường hợp cookie cũ không có domain
  try {
    const host = window.location.hostname;
    if (host && host !== "quantera.vn" && !host.endsWith(".quantera.vn")) {
      safeSetCookie(base);
    }
  } catch {
    // ignore
  }
}

function readCookieRaw(name) {
  if (typeof document === "undefined") return null;
  try {
    const match = document.cookie
      .split("; ")
      .find((row) => row.startsWith(`${name}=`));
    if (!match) return null;
    return decodeURIComponent(match.split("=").slice(1).join("="));
  } catch {
    return null;
  }
}

/** Đọc cookie_preferences_quantera */
export function getCookiePreferences() {
  const raw = readCookieRaw(PREFS_COOKIE_NAME);
  if (!raw) return null;
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

/** Chỉ cho phép cookie ngôn ngữ khi functional === true */
export function isFunctionalCookieAllowed() {
  try {
    return getCookiePreferences()?.functional === true;
  } catch {
    return false;
  }
}

/** Đọc ngôn ngữ từ cookie: "en" | "vi" | null (bỏ qua giá trị lạ như "ja") */
export function getAccessLangCookie() {
  const value = (readCookieRaw(LANG_COOKIE_NAME) || "").trim().toLowerCase();
  return value === "en" || value === "vi" ? value : null;
}

/**
 * Lưu ngôn ngữ vào cookie_accesslang_quantera.
 * Luôn đọc lại consent hiện tại (hỗ trợ cross-tab) trừ khi force=true.
 */
export function setAccessLangCookie(lang, { force = false } = {}) {
  if (typeof document === "undefined") return false;
  try {
    if (!force && !isFunctionalCookieAllowed()) return false;

    const normalized = String(lang || "").toLowerCase();
    if (normalized !== "en" && normalized !== "vi") return false;

    safeSetCookie(
      `${LANG_COOKIE_NAME}=${encodeURIComponent(normalized)}` +
        `; path=/; max-age=${MAX_AGE}; SameSite=Lax` +
        cookieDomainAttr()
    );
    return true;
  } catch {
    return false;
  }
}

/**
 * Xóa cookie ngôn ngữ theo key name (kể cả value lạ / đã hết hạn).
 * Domain scope .quantera.vn để dọn cả subdomain.
 */
export function clearAccessLangCookie() {
  expireCookieByName(LANG_COOKIE_NAME);
}

export function saveCookiePreferences(prefs) {
  if (typeof document === "undefined") return;
  try {
    safeSetCookie(
      `${PREFS_COOKIE_NAME}=${encodeURIComponent(JSON.stringify(prefs))}` +
        `; path=/; max-age=${MAX_AGE}; SameSite=Lax` +
        cookieDomainAttr()
    );
  } catch {
    // ignore
  }
}

/**
 * Áp dụng preferences sau khi user bấm Lưu / Accept / Reject.
 * functional true  → tạo/cập nhật cookie ngôn ngữ theo currentLang
 * functional false → xóa sạch cookie ngôn ngữ
 */
export function applyCookiePreferences(prefs, currentLang) {
  saveCookiePreferences(prefs);

  if (prefs?.functional === true) {
    setAccessLangCookie(currentLang, { force: true });
  } else {
    clearAccessLangCookie();
  }

  notifyCookieConsentChanged();
}

function notifyCookieConsentChanged() {
  try {
    // Cross-tab: tab khác nhận storage event rồi sync
    localStorage.setItem(CONSENT_SYNC_KEY, String(Date.now()));
  } catch {
    // localStorage bị chặn — vẫn dispatch trong tab hiện tại
  }
  try {
    window.dispatchEvent(new Event("cookie-consent-changed"));
  } catch {
    // ignore
  }
}

/** Lắng nghe thay đổi consent (cùng tab + cross-tab) */
export function subscribeCookieConsentChange(handler) {
  if (typeof window === "undefined") return () => {};

  const onLocal = () => handler();
  const onStorage = (e) => {
    if (e.key === CONSENT_SYNC_KEY) handler();
  };
  const onFocus = () => handler();

  window.addEventListener("cookie-consent-changed", onLocal);
  window.addEventListener("storage", onStorage);
  window.addEventListener("focus", onFocus);

  return () => {
    window.removeEventListener("cookie-consent-changed", onLocal);
    window.removeEventListener("storage", onStorage);
    window.removeEventListener("focus", onFocus);
  };
}

/**
 * Redirect: chỉ dùng cookie ngôn ngữ khi functional === true.
 * Nếu tắt functional / không có cookie hợp lệ → fallback.
 */
export function resolveAccessLang(fallback = "en") {
  if (!isFunctionalCookieAllowed()) return fallback;
  return getAccessLangCookie() || fallback;
}
